import { useQuery } from '@tanstack/react-query';
import { GetCustomizedDocumentTypesResponse } from '@ab/ams-school-api/src/services/studentDocuments/v1/customization/types';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import { hasKey } from '~/utils';

export function useGetCustomizedDocumentTypes() {
  const request = useRequest();

  const { data, error, isLoading } = useQuery(
    ['customized_document_types'], // Unique query key
    async () => {
      const response = await request<GetCustomizedDocumentTypesResponse>(
        `${config.apiHost}/private/v1/customized_document_types`,
        {
          method: 'GET',
          isExpectedResponse: isExpectedGetCustomizedDocumentTypesResponse,
        }
      );
      return response;
    }
  );

  return {
    customizedDocumentTypes: data?.data,
    getCustomizedDocumentTypesError: error,
    isFetchingCustomizedDocumentTypes: isLoading,
  };
}

function isExpectedGetCustomizedDocumentTypesResponse(res: unknown): res is GetCustomizedDocumentTypesResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}

type EducationHistoryDocumentTypeConfig = {
  type?: string;
  required?: boolean;
  AiProcessDocumentEnabled?: boolean;
  aiProcessDocumentEnabled?: boolean;
};

export function getTranscriptDocConfig(
  customizedDocumentTypes: { attributes: unknown } | undefined
): EducationHistoryDocumentTypeConfig | undefined {
  const docTypes = (
    customizedDocumentTypes?.attributes as
      | { educationHistoryDocumentTypes?: EducationHistoryDocumentTypeConfig[] }
      | undefined
  )?.educationHistoryDocumentTypes;

  return Array.isArray(docTypes) ? docTypes.find((d) => d.type === 'TRANSCRIPT') : undefined;
}

/**
 * Whether AI transcript processing is enabled for the school.
 * Derived from the TRANSCRIPT entry in educationHistoryDocumentTypes.
 * Defaults to true when no configuration is found.
 */
export function useTranscriptAiEnabled(): boolean {
  const { customizedDocumentTypes } = useGetCustomizedDocumentTypes();
  const transcriptDoc = getTranscriptDocConfig(customizedDocumentTypes);

  if (!transcriptDoc) return true;
  return transcriptDoc.AiProcessDocumentEnabled ?? transcriptDoc.aiProcessDocumentEnabled ?? true;
}

/**
 * Whether the grade scheme field is required for the school.
 * Derived from the `required` property of the TRANSCRIPT entry in educationHistoryDocumentTypes.
 * Defaults to false when no configuration is found.
 */
export function useGradeSchemeRequired(): boolean {
  const { customizedDocumentTypes } = useGetCustomizedDocumentTypes();
  const transcriptDoc = getTranscriptDocConfig(customizedDocumentTypes);

  return transcriptDoc?.required ?? false;
}
